/* Public "/book/:token" page — anyone with the share link can view the client's
   personal book. No auth needed. Embeds the PDF inline. */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { BookOpen, Download, Loader2, XCircle } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function PublicBook() {
  const { token } = useParams();
  const { t } = useLang();
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetch(`${API_BASE}/api/public/personal-book/${token}`)
      .then((r) => {
        if (!r.ok) throw new Error(t("book.public.revoked.title"));
        return r.json();
      })
      .then(setMeta)
      .catch((e) => setErr(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const pdfUrl = `${API_BASE}/api/public/personal-book/${token}/pdf`;

  return (
    <div className="min-h-screen pt-24 pb-20" style={{ backgroundColor: "#F3EBE1" }} data-testid="public-book-page">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-6">
          <BookOpen size={22} strokeWidth={1.3} className="text-[color:var(--accent)]" />
          <p className="overline text-[color:var(--accent)]">{t("book.public.title")}</p>
        </div>

        {err && (
          <div className="card-soft p-8 text-center">
            <XCircle size={32} strokeWidth={1.2} className="mx-auto text-[color:var(--accent-2)]" />
            <h1 className="font-serif text-3xl text-[color:var(--ink)] mt-4">{t("book.public.revoked.title")}</h1>
            <p className="mt-3 text-[color:var(--ink-2)]">{err}</p>
          </div>
        )}

        {!err && !meta && (
          <div className="card-soft p-8 text-center">
            <Loader2 size={32} strokeWidth={1.2} className="mx-auto text-[color:var(--accent)] animate-spin" />
            <p className="mt-4 text-[color:var(--ink-2)]">{t("book.public.loading")}</p>
          </div>
        )}

        {meta && (
          <>
            <h1 className="font-serif font-light text-4xl sm:text-5xl text-[color:var(--ink)] tracking-tight">
              <em className="text-[color:var(--accent)] not-italic">{meta.owner_name}</em>{t("book.public.owner_journey")}
            </h1>
            <p className="mt-3 text-[color:var(--ink-2)]">
              {meta.picture_count} {meta.picture_count === 1 ? t("book.public.pictures") : t("book.public.pictures_plural")}
              {" · "}
              {meta.journal_count} {meta.journal_count === 1 ? t("book.public.entries") : t("book.public.entries_plural")}
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={pdfUrl}
                download
                className="btn-primary"
                data-testid="public-book-download"
              >
                <Download size={14} strokeWidth={1.6} /> {t("book.public.download")}
              </a>
            </div>

            <div className="mt-8 rounded-2xl overflow-hidden border border-[color:var(--line-soft)] bg-[var(--bg)] shadow-sm" data-testid="public-book-viewer">
              <iframe
                src={pdfUrl}
                title={`${meta.owner_name}'s personal book`}
                className="w-full"
                style={{ height: "80vh", border: 0 }}
              />
            </div>

            <p className="mt-6 text-xs text-[color:var(--ink-3)] italic text-center">
              {t("book.public.footer")}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
